import { Vector3 } from 'three/webgpu';
import type { Motion } from './Attack';
import type { Weapon } from './weapons';

/** An enemy's body for hit tests: a capsule standing along `up` (a ball when height ≤ 2·radius). */
export interface Body {
  center: Vector3;
  up: Vector3;
  radius: number;
  height: number;
}

export interface Reached {
  /** The point on the body's axis the weapon came nearest to. */
  at: Vector3;
  /** It only grazed the body: a glancing hit (half damage, knockback and stun). */
  glancing: boolean;
  /** How far the weapon's line passed from the body's surface (m, ≤ 0 inside it). */
  miss: number;
}

/**
 * Past the blade's own width, this much more (m) still grazes the body: a glancing hit.
 * Beyond it, a miss.
 */
export const GLANCE = 0.4;

const _tip = new Vector3();
const _bottom = new Vector3();
const _top = new Vector3();
const _p = new Vector3();
const _q = new Vector3();

/**
 * Whether the weapon reaches a body, and how squarely. The weapon is a line from the eye out
 * to its reach, `blade` metres thick, along each view direction the strike passed through
 * (from the release to the hit, `Motion.path`): a swing sweeps through what the aim swept
 * through, no more. So the aim must be on the target, or pass over it, as the strike goes.
 * A moving target, or moving yourself, makes that hard. Within the blade: a hit. Within
 * GLANCE more: a glancing hit.
 */
export function reach(w: Weapon, m: Motion, b: Body): Reached | null {
  const half = Math.max(0, b.height / 2 - b.radius);
  _bottom.copy(b.center).addScaledVector(b.up, -half);
  _top.copy(b.center).addScaledVector(b.up, half);
  let best = Number.POSITIVE_INFINITY;
  const at = new Vector3();
  const path = m.path.length > 0 ? m.path : [m.forward];
  for (const dir of path) {
    _tip.copy(m.eye).addScaledVector(dir, w.reach);
    const d = closestBetweenSegments(m.eye, _tip, _bottom, _top, _p, _q) - b.radius;
    if (d < best) {
      best = d;
      at.copy(_q);
    }
  }
  if (best <= w.blade) return { at, glancing: false, miss: best };
  if (best <= w.blade + GLANCE) return { at, glancing: true, miss: best };
  return null;
}

const _d1 = new Vector3();
const _d2 = new Vector3();
const _r = new Vector3();

/**
 * The distance between segments p1–q1 and p2–q2, with the closest points on each written to
 * `c1` and `c2` (Ericson, Real-Time Collision Detection, 5.1.9).
 */
export function closestBetweenSegments(
  p1: Vector3,
  q1: Vector3,
  p2: Vector3,
  q2: Vector3,
  c1: Vector3,
  c2: Vector3,
): number {
  const d1 = _d1.subVectors(q1, p1);
  const d2 = _d2.subVectors(q2, p2);
  const r = _r.subVectors(p1, p2);
  const a = d1.dot(d1);
  const e = d2.dot(d2);
  const f = d2.dot(r);
  const EPS = 1e-9;
  let s: number;
  let t: number;
  if (a <= EPS && e <= EPS) {
    s = 0;
    t = 0;
  } else if (a <= EPS) {
    s = 0;
    t = clamp01(f / e);
  } else {
    const c = d1.dot(r);
    if (e <= EPS) {
      t = 0;
      s = clamp01(-c / a);
    } else {
      const bb = d1.dot(d2);
      const denom = a * e - bb * bb;
      s = denom > EPS ? clamp01((bb * f - c * e) / denom) : 0;
      t = (bb * s + f) / e;
      if (t < 0) {
        t = 0;
        s = clamp01(-c / a);
      } else if (t > 1) {
        t = 1;
        s = clamp01((bb - c) / a);
      }
    }
  }
  c1.copy(p1).addScaledVector(d1, s);
  c2.copy(p2).addScaledVector(d2, t);
  return c1.distanceTo(c2);
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
